// System historii i uczenia się z procesów gięcia
// Zapisuje udane parametry i uczy się z błędów

interface BendingHistoryEntry {
  id: string;
  timestamp: Date;
  materialId: string;
  thickness: number;
  bendLength: number;
  bendAngle: number;
  bendRadius: number;
  machineId: string;
  parameters: {
    upperHeaterTemp: number;
    lowerHeaterTemp: number;
    heatingTime: number;
    heatingZoneWidth: number;
    coolingTime: number;
  };
  result: 'success' | 'failure' | 'partial';
  defects?: string[];
  notes?: string;
  operator?: string;
  energyUsed?: number; // kWh
}

interface MaterialLearning {
  materialId: string;
  thickness: number;
  successfulParams: {
    upperTempAvg: number;
    lowerTempAvg: number;
    heatingTimeAvg: number;
    count: number;
  };
  commonDefects: Map<string, number>;
  bestPractices: string[];
}

export class BendingHistory {
  private static STORAGE_KEY = 'plexisystem_bending_history';
  private static LEARNING_KEY = 'plexisystem_bending_learning';
  private static MAX_HISTORY_ENTRIES = 500;
  
  /**
   * Zapisuje nowy wpis do historii
   */
  static addEntry(entry: Omit<BendingHistoryEntry, 'id' | 'timestamp'>): void {
    const newEntry: BendingHistoryEntry = {
      ...entry,
      id: `bend_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    };
    
    const history = this.getHistory();
    history.unshift(newEntry);
    
    // Ogranicz rozmiar historii
    if (history.length > this.MAX_HISTORY_ENTRIES) {
      history.pop();
    }
    
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(history));
    
    // Aktualizuj dane uczenia
    if (entry.result === 'success') {
      this.updateLearning(newEntry);
    }
  }
  
  /**
   * Pobiera całą historię
   */
  static getHistory(): BendingHistoryEntry[] {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored) return [];
    
    try {
      const history = JSON.parse(stored);
      // Konwertuj stringi dat na obiekty Date
      return history.map((entry: any) => ({
        ...entry,
        timestamp: new Date(entry.timestamp)
      }));
    } catch {
      return [];
    }
  }
  
  /**
   * Pobiera historię dla konkretnego materiału i grubości
   */
  static getHistoryForMaterial(
    materialId: string, 
    thickness: number,
    limit: number = 10
  ): BendingHistoryEntry[] {
    const history = this.getHistory();
    
    return history
      .filter(entry => 
        entry.materialId === materialId && 
        Math.abs(entry.thickness - thickness) <= 1 // Tolerancja ±1mm
      )
      .slice(0, limit);
  }
  
  /**
   * Znajduje podobne procesy w historii
   */
  static findSimilarProcesses(
    materialId: string,
    thickness: number,
    bendAngle: number,
    bendRadius: number
  ): BendingHistoryEntry[] {
    const history = this.getHistory();
    
    return history
      .filter(entry => 
        entry.materialId === materialId &&
        entry.result === 'success' &&
        Math.abs(entry.thickness - thickness) <= 2 &&
        Math.abs(entry.bendAngle - bendAngle) <= 15 &&
        Math.abs(entry.bendRadius - bendRadius) <= 3
      )
      .sort((a, b) => {
        // Sortuj po podobieństwie (im mniejsza różnica, tym lepiej)
        const diffA = 
          Math.abs(a.thickness - thickness) +
          Math.abs(a.bendAngle - bendAngle) +
          Math.abs(a.bendRadius - bendRadius);
        const diffB = 
          Math.abs(b.thickness - thickness) +
          Math.abs(b.bendAngle - bendAngle) +
          Math.abs(b.bendRadius - bendRadius);
        return diffA - diffB;
      })
      .slice(0, 5);
  }
  
  /**
   * Aktualizuje dane uczenia na podstawie udanego procesu
   */
  private static updateLearning(entry: BendingHistoryEntry): void {
    const learningData = this.getLearningData();
    const key = `${entry.materialId}_${entry.thickness}`;
    
    let learning = learningData.get(key);
    if (!learning) {
      learning = {
        materialId: entry.materialId,
        thickness: entry.thickness,
        successfulParams: {
          upperTempAvg: 0,
          lowerTempAvg: 0,
          heatingTimeAvg: 0,
          count: 0
        },
        commonDefects: new Map(),
        bestPractices: []
      };
    }
    
    // Aktualizuj średnie parametry (ważone)
    const params = learning.successfulParams;
    const weight = params.count / (params.count + 1);
    
    params.upperTempAvg = params.upperTempAvg * weight + 
      entry.parameters.upperHeaterTemp * (1 - weight);
    params.lowerTempAvg = params.lowerTempAvg * weight + 
      entry.parameters.lowerHeaterTemp * (1 - weight);
    params.heatingTimeAvg = params.heatingTimeAvg * weight + 
      entry.parameters.heatingTime * (1 - weight);
    params.count++;
    
    // Aktualizuj defekty (jeśli były)
    if (entry.defects) {
      entry.defects.forEach(defect => {
        const count = learning!.commonDefects.get(defect) || 0;
        learning!.commonDefects.set(defect, count + 1);
      });
    }
    
    learningData.set(key, learning);
    this.saveLearningData(learningData);
  }
  
  /**
   * Pobiera dane uczenia
   */
  static getLearningData(): Map<string, MaterialLearning> {
    const stored = localStorage.getItem(this.LEARNING_KEY);
    if (!stored) return new Map();
    
    try {
      const data = JSON.parse(stored);
      const map = new Map<string, MaterialLearning>();
      
      Object.entries(data).forEach(([key, value]: [string, any]) => {
        map.set(key, {
          ...value,
          commonDefects: new Map(Object.entries(value.commonDefects || {}))
        });
      });
      
      return map;
    } catch {
      return new Map();
    }
  }
  
  /**
   * Zapisuje dane uczenia
   */
  private static saveLearningData(data: Map<string, MaterialLearning>): void {
    const obj: Record<string, any> = {};
    
    data.forEach((value, key) => {
      obj[key] = {
        ...value,
        commonDefects: Object.fromEntries(value.commonDefects)
      };
    });
    
    localStorage.setItem(this.LEARNING_KEY, JSON.stringify(obj));
  }
  
  /**
   * Sugeruje parametry na podstawie historii
   */
  static suggestParameters(
    materialId: string,
    thickness: number,
    bendAngle: number
  ): {
    suggested: boolean;
    upperTemp?: number;
    lowerTemp?: number;
    heatingTime?: number;
    confidence: number;
    basedOn: number;
  } {
    // Najpierw sprawdź dane uczenia
    const learningData = this.getLearningData();
    const key = `${materialId}_${thickness}`;
    const learning = learningData.get(key);
    
    if (learning && learning.successfulParams.count >= 3) {
      // Mamy wystarczająco danych do sugestii
      return {
        suggested: true,
        upperTemp: Math.round(learning.successfulParams.upperTempAvg),
        lowerTemp: Math.round(learning.successfulParams.lowerTempAvg),
        heatingTime: Math.round(learning.successfulParams.heatingTimeAvg),
        confidence: Math.min(95, 60 + learning.successfulParams.count * 5),
        basedOn: learning.successfulParams.count
      };
    }
    
    // Jeśli nie ma dokładnych danych, szukaj podobnych
    const similarProcesses = this.findSimilarProcesses(
      materialId,
      thickness,
      bendAngle,
      2 // promień domyślny
    );
    
    if (similarProcesses.length >= 2) {
      // Oblicz średnie z podobnych procesów
      const avgUpper = similarProcesses.reduce(
        (sum, p) => sum + p.parameters.upperHeaterTemp, 0
      ) / similarProcesses.length;
      
      const avgLower = similarProcesses.reduce(
        (sum, p) => sum + p.parameters.lowerHeaterTemp, 0
      ) / similarProcesses.length;
      
      const avgTime = similarProcesses.reduce(
        (sum, p) => sum + p.parameters.heatingTime, 0
      ) / similarProcesses.length;
      
      return {
        suggested: true,
        upperTemp: Math.round(avgUpper),
        lowerTemp: Math.round(avgLower),
        heatingTime: Math.round(avgTime),
        confidence: Math.min(75, 40 + similarProcesses.length * 7),
        basedOn: similarProcesses.length
      };
    }
    
    return {
      suggested: false,
      confidence: 0,
      basedOn: 0
    };
  }
  
  /**
   * Pobiera statystyki dla materiału
   */
  static getMaterialStatistics(materialId: string): {
    totalProcesses: number;
    successRate: number;
    commonDefects: Array<{ name: string; count: number }>;
    averageEnergy: number;
    averageCycleTime: number;
  } {
    const history = this.getHistory();
    const materialHistory = history.filter(e => e.materialId === materialId);
    
    if (materialHistory.length === 0) {
      return {
        totalProcesses: 0,
        successRate: 0,
        commonDefects: [],
        averageEnergy: 0,
        averageCycleTime: 0
      };
    }
    
    const successful = materialHistory.filter(e => e.result === 'success').length;
    const successRate = (successful / materialHistory.length) * 100;
    
    // Zlicz defekty
    const defectCounts = new Map<string, number>();
    materialHistory.forEach(entry => {
      if (entry.defects) {
        entry.defects.forEach(defect => {
          const count = defectCounts.get(defect) || 0;
          defectCounts.set(defect, count + 1);
        });
      }
    });
    
    const commonDefects = Array.from(defectCounts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
    
    // Średnie zużycie energii
    const energyEntries = materialHistory.filter(e => e.energyUsed);
    const averageEnergy = energyEntries.length > 0
      ? energyEntries.reduce((sum, e) => sum + (e.energyUsed || 0), 0) / energyEntries.length
      : 0;
    
    // Średni czas cyklu
    const averageCycleTime = materialHistory.reduce(
      (sum, e) => sum + e.parameters.heatingTime + e.parameters.coolingTime, 0
    ) / materialHistory.length;
    
    return {
      totalProcesses: materialHistory.length,
      successRate: Math.round(successRate),
      commonDefects,
      averageEnergy: Math.round(averageEnergy * 100) / 100,
      averageCycleTime: Math.round(averageCycleTime)
    };
  }
  
  /**
   * Eksportuje historię do CSV
   */
  static exportToCSV(): string {
    const history = this.getHistory();
    
    const headers = [
      'Data',
      'Materiał',
      'Grubość (mm)',
      'Długość gięcia (mm)',
      'Kąt (°)',
      'Promień (mm)',
      'Maszyna',
      'Temp. górna (°C)',
      'Temp. dolna (°C)',
      'Czas grzania (s)',
      'Czas chłodzenia (s)',
      'Rezultat',
      'Defekty',
      'Uwagi',
      'Operator',
      'Energia (kWh)'
    ].join(',');
    
    const rows = history.map(entry => [
      entry.timestamp.toLocaleDateString('pl-PL'),
      entry.materialId,
      entry.thickness,
      entry.bendLength,
      entry.bendAngle,
      entry.bendRadius,
      entry.machineId,
      entry.parameters.upperHeaterTemp,
      entry.parameters.lowerHeaterTemp,
      entry.parameters.heatingTime,
      entry.parameters.coolingTime,
      entry.result,
      entry.defects?.join('; ') || '',
      entry.notes || '',
      entry.operator || '',
      entry.energyUsed || ''
    ].join(','));
    
    return [headers, ...rows].join('\n');
  }
  
  /**
   * Czyści historię
   */
  static clearHistory(): void {
    if (confirm('Czy na pewno chcesz usunąć całą historię gięcia? Tej operacji nie można cofnąć.')) {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.LEARNING_KEY);
    }
  }
}